import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Users, DollarSign, ArrowRight, TrendingUp, Clock } from 'lucide-react';
import { Button } from '../ui/button';

export function AffiliateBanner() {
  const { t } = useTranslation();
  
  return (
    <section 
      className="relative py-16 overflow-hidden"
      data-testid="affiliate-banner"
    >
      {/* Dark Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#1a1a1a] to-[#0A0A0A]" />
      
      {/* Animated Grid Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(#C25E30 1px, transparent 1px), linear-gradient(90deg, #C25E30 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }} />
      </div>
      
      {/* Glow Effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#C25E30]/20 rounded-full blur-[100px]" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[#1E3A8A]/20 rounded-full blur-[100px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col lg:flex-row items-center justify-between gap-10"
        >
          {/* Left Content */}
          <div className="flex-1 text-center lg:text-left">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 bg-[#C25E30]/10 text-[#C25E30] px-4 py-2 rounded-full text-sm font-semibold mb-5"
            >
              <Users className="w-4 h-4" />
              {t('home.affiliate.badge')}
            </motion.div>
            
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4">
              {t('home.affiliate.title')}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#C25E30] to-[#E8A54B]">
                Afriqoma
              </span>
            </h3>
            
            <p className="text-white/60 text-lg max-w-xl mx-auto lg:mx-0">
              {t('home.affiliate.description')}{' '}
              <span className="text-[#C25E30] font-semibold">{t('home.affiliate.commission')}</span>{' '}
              {t('home.affiliate.descriptionMid')}{' '}
              <span className="text-white font-medium">{t('home.affiliate.recurring')}</span>
            </p>
          </div>

          {/* Right Content - Stats & CTA */}
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* Stats Cards */}
            <div className="flex gap-4">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center min-w-[120px]">
                <TrendingUp className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">5%</p>
                <p className="text-xs text-white/50">{t('home.affiliate.commissionLabel')}</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center min-w-[120px]">
                <DollarSign className="w-6 h-6 text-[#C25E30] mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">$20</p>
                <p className="text-xs text-white/50">{t('home.affiliate.minPayout')}</p>
              </div>
              <div className="hidden sm:block bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center min-w-[120px]">
                <Clock className="w-6 h-6 text-[#1E3A8A] mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">24h</p>
                <p className="text-xs text-white/50">{t('home.affiliate.approval')}</p>
              </div>
            </div>
            
            {/* CTA Button */}
            <Link to="/affiliate/register">
              <Button 
                size="lg" 
                className="bg-[#C25E30] hover:bg-[#A64D24] text-white gap-3 px-8 py-6 rounded-xl font-semibold btn-premium whitespace-nowrap"
                data-testid="affiliate-signup-btn"
              >
                {t('home.affiliate.becomePartner')}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
